import { useState } from "react";
import { ImageOff } from "lucide-react";
import type { OrderItem } from "@aurelia/types";
import { PriceTag } from "@aurelia/ui";

function variantLabel(item: OrderItem): string {
  return [item.colorSnapshot, item.sizeSnapshot].filter(Boolean).join(" / ");
}

export function OrderProductRow({ item, children }: { item: OrderItem; children?: React.ReactNode }) {
  const [imageFailed, setImageFailed] = useState(false);
  const variant = variantLabel(item);
  const showImage = Boolean(item.imageUrl) && !imageFailed;

  return (
    <div className="grid min-w-0 grid-cols-[72px_minmax(0,1fr)] gap-3 py-4 sm:grid-cols-[88px_minmax(0,1fr)_auto] sm:gap-4">
      <div className="h-[72px] w-[72px] overflow-hidden rounded-xl border border-border bg-surface sm:h-[88px] sm:w-[88px]">
        {showImage ? (
          <img
            src={item.imageUrl!}
            alt={item.productNameSnapshot}
            loading="lazy"
            decoding="async"
            onError={() => setImageFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-ink-muted" role="img" aria-label="Product image unavailable">
            <ImageOff size={20} aria-hidden="true" />
            <span className="text-[10px]">No image</span>
          </div>
        )}
      </div>

      <div className="min-w-0">
        <p className="break-words text-sm font-medium leading-snug text-ink sm:text-base">{item.productNameSnapshot}</p>
        {variant && <p className="mt-1 text-xs text-ink-muted sm:text-sm">Variant: {variant}</p>}
        {item.skuSnapshot && <p className="mt-0.5 break-all text-xs text-ink-muted">SKU: {item.skuSnapshot}</p>}
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-muted sm:text-sm">
          <span>Qty: {item.quantity}</span>
          <span><PriceTag cents={item.unitPrice} className="text-xs sm:text-sm" /> each</span>
        </div>
        {children}
      </div>

      <div className="col-span-2 flex items-center justify-between border-t border-border/70 pt-3 sm:col-span-1 sm:block sm:border-0 sm:pt-0 sm:text-right">
        <span className="text-xs text-ink-muted sm:block">Item subtotal</span>
        <PriceTag cents={item.unitPrice * item.quantity} className="text-sm sm:mt-1 sm:text-base" />
      </div>
    </div>
  );
}
